import { useMemo } from 'react';
import { useColorModeValue, useToken } from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DataBox } from 'src/components/molecules/DataBox';
import {
  ValueTickFormatter,
  KeyMetricsDateFormatter,
} from 'src/utils/TickFormatters';
import { ApiForecast } from 'src/api/queries';

type ChannelContributionChartProps = {
  forecast: ApiForecast;
  scenarioIndex?: number;
};

const CHANNEL_COLORS = [
  '#3182CE',
  '#E53E3E',
  '#38A169',
  '#D69E2E',
  '#805AD5',
  '#DD6B20',
  '#319795',
  '#D53F8C',
  '#718096',
  '#00B5D8',
  '#ED64A6',
  '#4FD1C5',
];

export const ChannelContributionChart: React.FC<
  ChannelContributionChartProps
> = ({ forecast, scenarioIndex = 0 }) => {
  const [panelDark, borderDark] = useToken('colors', [
    'figmaDark.panel',
    'figmaDark.borderSubtle',
  ]);
  const tooltipBg = useColorModeValue('white', panelDark);
  const tooltipBorder = useColorModeValue('#ccc', borderDark);
  const tooltipText = useColorModeValue('#14171F', '#ECEDEE');
  const hoverCursorFill = useColorModeValue(
    'rgba(0, 0, 0, 0.05)',
    'rgba(255, 255, 255, 0.08)',
  );
  const scenario = forecast.scenarios[scenarioIndex];

  const { chartData, channelNames } = useMemo(() => {
    if (!scenario || scenario.results.length === 0) {
      return { chartData: [], channelNames: [] };
    }

    const names = new Set<string>();
    for (const result of scenario.results) {
      for (const ch of result.channels) {
        if (ch.channelEffect > 0) {
          names.add(ch.mediaChannel);
        }
      }
    }
    const sortedNames = Array.from(names).sort();

    const data = scenario.results.map((result) => {
      const row: Record<string, number | string> = {
        date: result.periodDate,
      };
      for (const name of sortedNames) {
        const ch = result.channels.find((c) => c.mediaChannel === name);
        row[name] = ch?.channelEffect ?? 0;
      }
      return row;
    });

    return { chartData: data, channelNames: sortedNames };
  }, [scenario]);

  if (chartData.length === 0) return null;

  return (
    <DataBox
      title={`Channel Contribution — ${scenario.scenarioName}`}
      colSpan={12}
      minH="350px"
      isError={false}
      isLoading={false}
      hasData={chartData.length > 0}>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis
            dataKey="date"
            tickFormatter={KeyMetricsDateFormatter}
            fontSize={12}
          />
          <YAxis tickFormatter={ValueTickFormatter} fontSize={12} />
          <Tooltip
            labelFormatter={KeyMetricsDateFormatter}
            formatter={(value: number) => value.toLocaleString()}
            cursor={{ fill: hoverCursorFill }}
            contentStyle={{
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
            }}
            labelStyle={{ color: tooltipText }}
          />
          <Legend />
          {channelNames.map((name, idx) => (
            <Bar
              key={name}
              dataKey={name}
              stackId="stack"
              fill={CHANNEL_COLORS[idx % CHANNEL_COLORS.length]}
              name={name}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </DataBox>
  );
};
